import type { Channel } from '@/api/fastchannel';

/**
 * Channel playout modes.
 *
 * The control plane stores a free-form `channel.repeat_mode` (no CHECK) and the
 * packager honors the values below. The UI presents them as a single mode
 * selector instead of the older `repeat` boolean + legacy mode names:
 *
 * - `loop`    — 24/7 carousel: loop the lineup in order, forever (packager default).
 * - `once`    — play the lineup through once, then show the slate.
 * - `shuffle` — loop the lineup, randomizing the order each cycle.
 * - `epg`     — absolute start times (a real EPG grid). The packager plays each
 *               program at its wall-clock Start; gaps play the slate (no loop).
 *
 * `loop`/`once`/`shuffle` are *playlist* modes: playout follows the schedule
 * *order*, not wall-clock start times, so the editor is a drag-reorder list.
 * `epg` keeps the time-grid editor (absolute start times, gaps/overlaps).
 */
export type PlayoutMode = 'loop' | 'once' | 'shuffle' | 'epg';

export const PLAYOUT_MODES: PlayoutMode[] = ['loop', 'once', 'shuffle', 'epg'];

export const DEFAULT_PLAYOUT_MODE: PlayoutMode = 'loop';

interface PlayoutModeMeta {
  value: PlayoutMode;
  label: string;
  /** One line of helper text shown under the selector. */
  help: string;
}

export const PLAYOUT_MODE_META: Record<PlayoutMode, PlayoutModeMeta> = {
  loop: {
    value: 'loop',
    label: 'Loop',
    help: '24/7 carousel — play the lineup in order, then start over.',
  },
  once: {
    value: 'once',
    label: 'Play once',
    help: 'Play through the lineup once, then show the slate.',
  },
  shuffle: {
    value: 'shuffle',
    label: 'Shuffle',
    help: 'Loop the lineup, randomizing the order on each cycle.',
  },
  epg: {
    value: 'epg',
    label: 'EPG (absolute times)',
    help: 'Schedule each program at an absolute wall-clock time; gaps play the slate.',
  },
};

const KNOWN_MODES = new Set<string>(PLAYOUT_MODES);

/**
 * Resolve a channel's playout mode from its stored `repeat`/`repeat_mode`.
 *
 * Backward compatible with the legacy model: a channel with `repeat === false`
 * (or a missing/blank mode) reads as the carousel default, except an explicit
 * `once`/`none`-with-no-repeat maps to play-once. Legacy mode names
 * (`sameday`/`overday`/`none`) collapse to `loop`.
 */
export function resolvePlayoutMode(
  channel: Pick<Channel, 'repeat' | 'repeat_mode'>,
): PlayoutMode {
  const raw = (channel.repeat_mode ?? '').trim().toLowerCase();
  if (KNOWN_MODES.has(raw)) return raw as PlayoutMode;
  // Legacy fallback: an explicit non-repeat channel played once; otherwise loop.
  if (channel.repeat === false) return 'once';
  return DEFAULT_PLAYOUT_MODE;
}

/** Playlist modes follow schedule *order*, not absolute start times. */
export function isPlaylistMode(mode: PlayoutMode): boolean {
  return mode !== 'epg';
}

/**
 * Whether playout loops the lineup. Drives the "now playing" math: `loop` and
 * `shuffle` loop; `once` plays through and stops; `epg` plays at absolute times
 * (no loop) so now-playing matches a program's wall-clock window or shows a gap.
 */
export function modeLoops(mode: PlayoutMode): boolean {
  return mode !== 'once' && mode !== 'epg';
}

/** Map a selected mode back to the control-plane channel fields. */
export function playoutModeToInput(mode: PlayoutMode): {
  repeat: boolean;
  repeat_mode: PlayoutMode;
} {
  return { repeat: modeLoops(mode), repeat_mode: mode };
}

/** Short label for tables/badges, e.g. "Loop" / "Play once". */
export function playoutModeLabel(
  channel: Pick<Channel, 'repeat' | 'repeat_mode'>,
): string {
  return PLAYOUT_MODE_META[resolvePlayoutMode(channel)].label;
}
